/**
 * Mock Legal ID Verification Component
 *
 * A mock capture flow for testing legal ID verification SDK integration.
 * - Page 1: Start verification button
 * - Page 2: Live camera preview with manual front/back capture
 * - Page 3: Mock scan complete with success/failure options
 *
 * Uses postMessage protocol identical to real SDK for seamless testing.
 *
 * Success payload mirrors the Athena /v1/verifications/legal-ids/{id} response shape
 * (see athena/veriff-decision-webhook/src/test/resources/test-fixtures/veriff-decision-passport-0.json).
 */

import { useEffect, useRef, useState } from "react";
import { logger } from "../../shared/utils/logger";
import { initAnalytics } from "../../shared/analytics/analyticsClient";
import { resolveParentOrigin } from "../../shared/utils/parentOrigin";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import { IntroScreen } from "../legalIdVerification/screens/IntroScreen";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";

type Page = "intro" | "capture" | "outcomes";

/**
 * Mirrors the live Veriff document-capture guide: a grey capture frame with a mint ID
 * card outline, drawn over the camera preview, with a MOCK label in the frame corner.
 */
function MockCaptureOverlay() {
  return (
    <svg className={styles.mockCaptureOverlay} viewBox="0 0 1535 863" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect x="140" y="50" width="1255" height="760" rx="26" className={styles.mockCaptureFrame} />
      <g className={styles.mockCaptureCard}>
        <rect x="447" y="222" width="641" height="414" rx="24" />
        <circle cx="620" cy="380" r="62" />
        <path d="M537 545 a83 83 0 0 1 166 0" />
        <line x1="800" y1="348" x2="995" y2="348" />
        <line x1="865" y1="420" x2="995" y2="420" />
      </g>
      <text x="1350" y="768" textAnchor="end" className={styles.mockCaptureLabel}>MOCK</text>
    </svg>
  );
}

// The mock reuses the real IntroScreen, whose page view emits on mount. Without an
// initialized client that emit reports a never_initialized setup failure to Sentry
// on every mock session, drowning the alert real wiring bugs rely on. The client
// starts disabled and the mock never enables it, so the events are dropped quietly.
function initMockAnalytics() {
  try {
    initAnalytics({ module: "legal_id" });
  } catch (err) {
    logger.warn("[Mock SDK] analytics init failed; continuing without analytics", err);
  }
  return null;
}

export function LegalIdVerificationMock() {
  // During the first render, not in an effect: IntroScreen's page-view effect runs
  // before any effect of this parent would.
  useState(initMockAnalytics);
  const { consumer } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [page, setPage] = useState<Page>("intro");
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const [fallbackAvailable, setFallbackAvailable] = useState(false);
  const [testPersonaPhone, setTestPersonaPhone] = useState("");
  const [captureCount, setCaptureCount] = useState(0);
  // Only digits reach IVA, so "abc" must not enable the button and send the default persona.
  const testPersonaDigits = testPersonaPhone.replace(/\D/g, "");
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isMountedRef = useRef(true);
  const isRequestingCameraRef = useRef(false);
  const pageRef = useRef<Page>(page);

  useEffect(() => {
    try {
      // Resolve against the IDMe allowlist, same as the real SDK — never post a
      // verification result to an origin we don't recognise.
      const origin = resolveParentOrigin();
      if (!origin) {
        logger.log("[Mock SDK] Standalone mode - no allowlisted parent origin");
        return;
      }

      setParentOrigin(origin);

      // Signal to parent that we're ready
      window.parent.postMessage(
        {
          source: "idme-verification",
          type: "IDME_READY",
        },
        origin
      );

      logger.log("[Mock SDK] IDME_READY sent to parent, origin:", origin);

      // Listen for IDME_INIT from parent
      const handleInitMessage = (event: MessageEvent) => {
        if (event.data?.type !== "IDME_INIT") {
          return;
        }

        if (event.origin !== origin) {
          logger.warn("[Mock SDK] Ignoring IDME_INIT from unexpected origin:", event.origin);
          return;
        }

        logger.log("[Mock SDK] Received IDME_INIT:", event.data.payload);
        setFallbackAvailable(event.data.payload?.fallbackAvailable === true);
        window.removeEventListener("message", handleInitMessage);
      };

      window.addEventListener("message", handleInitMessage);

      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (error) {
      logger.error("[Mock SDK] Initialization error:", error);
    }
  }, []);

  useEffect(() => {
    if (page === "capture" && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [page]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const postError = (errorCode: "camera_permission_denied" | "doc_capture_failed" | "user_no_id", message: string) => {
    // No allowlisted parent (e.g. the mock opened directly for local testing): there
    // is no host to deliver to, and posting to an unknown origin is what the allowlist
    // prevents, so only log it.
    if (!parentOrigin) {
      logger.warn("[Mock SDK] No allowlisted parent; VERIFICATION_ERROR not delivered:", errorCode);
      return;
    }

    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: { errorCode, message },
      },
      parentOrigin
    );
  };

  const handleOpenCamera = async () => {
    // Ignore repeat clicks while the permission prompt is pending, otherwise the
    // first stream is orphaned and the camera stays on.
    if (isRequestingCameraRef.current) {
      return;
    }
    isRequestingCameraRef.current = true;
    logger.log("[Mock SDK] Requesting camera permission");

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException("Camera is unavailable", "NotFoundError");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // A grant can land after the member has moved on (e.g. Skip Camera while the
      // prompt was open); don't pull them back to capture with the camera on.
      if (!isMountedRef.current || pageRef.current !== "intro") {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = stream;
      setCaptureCount(0);
      setPage("capture");
    } catch (error) {
      const errorName = typeof error === "object" && error !== null && "name" in error
        ? String(error.name)
        : "";
      if (errorName === "NotAllowedError" || errorName === "SecurityError") {
        postError("camera_permission_denied", "Camera permission denied");
        return;
      }

      postError("doc_capture_failed", "Camera is unavailable or could not be read");
    } finally {
      isRequestingCameraRef.current = false;
    }
  };

  const handleCaptureImage = () => {
    // The mock only tracks that front/back were captured; frames are never
    // returned to IVA, so don't hold ID images in memory.
    const nextCaptureCount = captureCount + 1;
    setCaptureCount(nextCaptureCount);

    if (nextCaptureCount === 2) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setPage("outcomes");
    }
  };

  const handleBackFromCapture = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCaptureCount(0);
    setPage("intro");
  };

  const handleFallback = () => {
    postError("user_no_id", "User requested another identity verification method");
  };

  const handleSkipCamera = () => {
    setCaptureCount(0);
    setPage("outcomes");
  };

  const handleCompleteVerification = (mockPersonaPhone?: string) => {
    logger.log("[Mock SDK] Sending VERIFICATION_COMPLETE");

    if (parentOrigin) {
      // Payload mirrors Athena GET /v1/verifications/legal-ids/{id} response shape
      // Based on veriff-decision-passport-0.json fixture
      window.parent.postMessage(
        {
          source: "idme-verification",
          type: "VERIFICATION_COMPLETE",
          payload: {
            ...(mockPersonaPhone ? { mockPersonaPhone } : {}),
            data: {
              uuid: "mock-legal-id-uuid",
              status: "completed",
              outcome: "passed",
              reason: {
                code: 200,
                name: "DOCUMENT_VERIFIED",
                description: "Document successfully verified",
              },
              results: {
                provider: "VERIFF",
                resources: [
                  {
                    uuid: "mock-credential-uuid",
                    resource: "legalId",
                    href: "/v1/credentials/legal-ids/mock-credential-uuid",
                  },
                ],
                attributes: {
                  nameFirst: "Jane",
                  nameLast: "Doe",
                  dateOfBirth: "1990-01-15",
                },
                providerData: {
                  status: "success",
                  verification: {
                    id: "mock-veriff-session-id",
                    status: "approved",
                    document: {
                      type: "PASSPORT",
                      country: "US",
                      number: "L898902C",
                      validUntil: "2029-07-08",
                    },
                  },
                },
              },
            },
          },
        },
        parentOrigin
      );
    }

    setTimeout(() => {
      window.close();
    }, 100);
  };

  const handleFailVerification = () => {
    logger.log("[Mock SDK] Sending VERIFICATION_ERROR");
    postError("doc_capture_failed", "Mock legal ID verification failed");

    setTimeout(() => {
      window.close();
    }, 100);
  };

  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(" ")}>
      <div className={styles.container}>
        <header className={styles.logoContainer}>
          <div className={styles.logo}>
            {consumerLogo ? (
              <img src={consumerLogo.src} alt={consumerLogo.alt} />
            ) : (
              <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
            )}
          </div>
        </header>

        <main className={styles.contentContainer}>
          {page === "intro" ? (
            <IntroScreen
              onOpenCamera={handleOpenCamera}
              onFallback={handleFallback}
              fallbackAvailable={fallbackAvailable}
              consumerLogo={consumerLogo}
              consumerConfig={consumerConfig}
            >
              <button className={styles.noIdLink} onClick={handleSkipCamera}>
                Skip Camera
              </button>
            </IntroScreen>
          ) : page === "capture" ? (
            <div className={styles.card}>
              <h1 className={styles.title}>
                {captureCount === 0 ? "Take a photo of your document’s photo page" : "Take a photo of the back of your document"}
              </h1>
              <p className={styles.subtitle}>Accepted documents: driver’s license, residence permit, passport, ID card.</p>
              <div className={styles.mockCameraPreview}>
                <video ref={videoRef} autoPlay muted playsInline aria-label="Camera preview" />
                <MockCaptureOverlay />
              </div>
              <button className={styles.openCameraButton} onClick={handleCaptureImage}>
                Take photo
              </button>
              <button className={styles.noIdLink} onClick={handleBackFromCapture}>
                Back
              </button>
            </div>
          ) : (
            <div className={styles.card}>
              <h1 className={styles.title}>Choose mock outcome</h1>
              <p className={styles.subtitle}>Document images captured. Select the result to return to IVA.</p>
              <button className={styles.openCameraButton} onClick={() => handleCompleteVerification()}>
                Complete verification
              </button>
              <button className={styles.mockOutcomeButton} onClick={handleFailVerification}>
                Fail verification
              </button>
              <div className={styles.mockPersonaLookup}>
                <label htmlFor="mock-persona-phone">Lookup Test Persona:</label>
                <input
                  id="mock-persona-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(888) 789-1962"
                  value={testPersonaPhone}
                  onChange={(event) => setTestPersonaPhone(event.target.value)}
                />
                <button
                  className={styles.openCameraButton}
                  disabled={!testPersonaDigits}
                  onClick={() => handleCompleteVerification(testPersonaDigits)}
                >
                  Verify as Test Persona
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
